import {
  ExtrudeGeometry,
  InstancedMesh,
  LatheGeometry,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Shape,
  Vector2,
} from 'three';
import { Layer } from './Layer.js';
import { Helix } from '../geometry/spiral.js';
import { circleProfile, rectProfile, sweepHelix } from '../geometry/sweep.js';
import { stoneTexture } from '../geometry/textures.js';

/**
 * "…a city where the buildings have spiral staircases…"
 *
 * A single flight winds down the inside of a shaft 232 m deep around an open
 * well, so the traveller can always see the turns above and the turns below:
 * the stair is its own horizon. Treads, fascia, balustrade and plinth are all
 * read off the one helix in `ctx.helix`, which is also what the camera walks
 * and what the encrustation clings to.
 *
 * Nothing here knows about shells, telescopes or sound.
 */
export class SpiralStaircase extends Layer {
  static id = 'staircase';
  static order = 10;

  async build() {
    const { settings, palette, helix } = this.ctx;
    const s = settings.stair;
    this.cfg = s;

    const bump = stoneTexture(512);
    bump.repeat.set(8, 2);

    this.stoneMat = new MeshStandardMaterial({
      color: palette.get('stone').clone(),
      roughness: 0.9,
      metalness: 0.02,
      bumpMap: bump,
      bumpScale: 0.22,
      roughnessMap: bump,
    });
    this.deepMat = new MeshStandardMaterial({
      color: palette.get('stoneDeep').clone(),
      roughness: 0.95,
      metalness: 0.0,
      bumpMap: bump,
      bumpScale: 0.4,
    });
    this.railMat = new MeshStandardMaterial({
      color: palette.get('rail').clone(),
      roughness: 0.4,
      metalness: 0.6,
    });

    this.railRadius = s.innerRadius + 0.22;

    this._buildTreads(helix, s);
    this._buildFascia(helix, s);
    this._buildBalustrade(helix, s);
    this._buildPlinth(helix, s);
  }

  /** One tapered wedge, instanced the whole way down. */
  _buildTreads(helix, s) {
    const total = Math.round(s.turns * s.stepsPerTurn);
    this.stepCount = total;
    const span = (Math.PI * 2) / s.stepsPerTurn;

    const shape = new Shape();
    const gap = span * 0.03; // a hair of shadow between treads
    const a0 = -span / 2 + gap;
    const a1 = span / 2 - gap;
    shape.absarc(0, 0, s.innerRadius - 0.05, a0, a1, false);
    shape.absarc(0, 0, s.outerRadius, a1, a0, true);
    shape.closePath();

    const geo = new ExtrudeGeometry(shape, {
      depth: s.treadThickness,
      bevelEnabled: true,
      bevelThickness: 0.03,
      bevelSize: 0.035,
      bevelSegments: 2,
      curveSegments: 14,
    });
    geo.rotateX(Math.PI / 2); // lie flat, extruded downward
    geo.computeVertexNormals();

    const mesh = new InstancedMesh(geo, this.stoneMat, total);
    mesh.frustumCulled = false;
    const dummy = new Object3D();
    for (let i = 0; i < total; i++) {
      const t = i / (total - 1);
      dummy.position.set(0, helix.heightAt(t), 0);
      dummy.rotation.set(0, -helix.angleAt(t), 0);
      // A stair this old is not quite level anywhere.
      dummy.position.y += Math.sin(i * 1.73) * 0.006;
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    this.treads = mesh;
    this.group.add(mesh);
  }

  /**
   * The fascia: the deep ribbon hanging below the inner edge. From anywhere
   * on the stair this is the band you see spiralling across the well, above
   * and below — so it is the surface the shells most want.
   */
  _buildFascia(helix, s) {
    const geo = sweepHelix(helix, {
      profile: rectProfile(0.4, s.fasciaDepth, 0.12),
      radius: s.innerRadius - 0.14,
      yOffset: -s.fasciaDepth * 0.5 - 0.05,
      steps: Math.round(s.turns * 170),
      caps: true,
    });
    const mesh = new Mesh(geo, this.deepMat);
    mesh.frustumCulled = false;
    this.fascia = mesh;
    this.group.add(mesh);

    // A torus moulding along the lower lip, catching the last light.
    const lip = sweepHelix(helix, {
      profile: circleProfile(0.16, 10, 0.8),
      radius: s.innerRadius - 0.14,
      yOffset: -s.fasciaDepth - 0.05,
      steps: Math.round(s.turns * 150),
      caps: false,
    });
    this.group.add(new Mesh(lip, this.stoneMat));
  }

  /** The balustrade on the well side: the thing you lean on to look down. */
  _buildBalustrade(helix, s) {
    const rail = sweepHelix(helix, {
      profile: circleProfile(s.railRadius, 12, 0.85),
      radius: this.railRadius,
      yOffset: s.railHeight,
      steps: Math.round(s.turns * 170),
      caps: true,
    });
    const mesh = new Mesh(rail, this.railMat);
    mesh.frustumCulled = false;
    this.rail = mesh;
    this.group.add(mesh);

    const low = sweepHelix(helix, {
      profile: circleProfile(s.railRadius * 0.55, 8, 0.9),
      radius: this.railRadius,
      yOffset: s.railHeight * 0.42,
      steps: Math.round(s.turns * 140),
      caps: true,
    });
    this.group.add(new Mesh(low, this.railMat));

    // Turned balusters.
    const k = s.balusterRadius / 0.05;
    const profile = [
      [0.0, 0.0],
      [0.082, 0.0],
      [0.07, 0.05],
      [0.04, 0.16],
      [0.072, 0.3],
      [0.055, 0.44],
      [0.03, 0.62],
      [0.055, 0.8],
      [0.045, 0.92],
      [0.068, 0.975],
      [0.055, 1.0],
      [0.0, 1.0],
    ].map(([x, y]) => new Vector2(x * k, y));
    const geo = new LatheGeometry(profile, 10);

    const total = Math.floor(this.stepCount / s.balusterEvery);
    const balusters = new InstancedMesh(geo, this.railMat, total);
    balusters.frustumCulled = false;
    const dummy = new Object3D();
    for (let i = 0; i < total; i++) {
      const t = (i * s.balusterEvery) / (this.stepCount - 1);
      if (t > 1) break;
      const a = helix.angleAt(t);
      dummy.position.set(
        Math.cos(a) * this.railRadius,
        helix.heightAt(t),
        Math.sin(a) * this.railRadius,
      );
      dummy.rotation.set(0, -a, 0);
      dummy.scale.set(1, s.railHeight, 1);
      dummy.updateMatrix();
      balusters.setMatrixAt(i, dummy.matrix);
    }
    balusters.instanceMatrix.needsUpdate = true;
    this.balusters = balusters;
    this.group.add(balusters);
  }

  /** Where the stair meets the shaft: a bench course along the wall. */
  _buildPlinth(helix, s) {
    const geo = sweepHelix(helix, {
      profile: rectProfile(0.8, 0.46, 0.09),
      radius: s.outerRadius - 0.3,
      yOffset: 0.2,
      steps: Math.round(s.turns * 150),
      caps: true,
    });
    const mesh = new Mesh(geo, this.stoneMat);
    mesh.frustumCulled = false;
    this.plinth = mesh;
    this.group.add(mesh);

    // A string course a little above head height, to give the wall a horizon.
    const course = sweepHelix(helix, {
      profile: rectProfile(0.42, 0.3, 0.07),
      radius: s.outerRadius + 0.15,
      yOffset: 2.55,
      steps: Math.round(s.turns * 150),
      caps: true,
    });
    this.group.add(new Mesh(course, this.deepMat));
  }

  update() {
    const { palette } = this.ctx;
    this.stoneMat.color.copy(palette.get('stone'));
    this.deepMat.color.copy(palette.get('stoneDeep'));
    this.railMat.color.copy(palette.get('rail'));
  }

  stats() {
    return { treads: this.stepCount, balusters: this.balusters?.count ?? 0 };
  }
}
